import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { SalesTeamService } from '@vendasta/sales';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { UsersService } from '../../../../data-services/users.service';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class AssignSalesTeamService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    protected readonly salesTeamService: SalesTeamService,
    protected readonly usersService: UsersService,
    protected readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  getUserNames(userIds: string[]): Observable<string[]> {
    const filteredUserIds = userIds.filter((id) => !!id);
    return this.usersService.getUsersById(filteredUserIds).pipe(
      map((users) =>
        userIds.map(
          (id) =>
            this.usersService.getUserDisplay(users, id) ||
            this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.A_SALESPERSON'),
        ),
      ),
      take(1),
      shareReplay(1),
    );
  }

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const stepData = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};

    const userId = activityData?.assigned_salesperson_unified_user_id;
    const previousUserId = activityData?.previous_assigned_salesperson_unified_user_id;
    const names$ = this.getUserNames([userId, previousUserId]);

    if (!!userId && stepData?.should_reassign) {
      return names$.pipe(
        switchMap((names) => {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.REASSIGNED_SALESPERSON', {
            salespersonName: names[0],
            previousSalespersonName: names[1],
          });
        }),
      );
    }
    if (!!userId && !stepData?.should_reassign) {
      return names$.pipe(
        switchMap((names) =>
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.ASSIGNED_SALESPERSON', {
            salespersonName: names[0],
          }),
        ),
      );
    }
    if (!userId && !stepData?.should_reassign) {
      return names$.pipe(
        switchMap((names) =>
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.ALREADY_ASSIGNED', {
            previousSalespersonName: names[1],
          }),
        ),
      );
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.ASSIGNED_SALESPERSON', {
      salespersonName: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.A_SALESPERSON'),
    });
  }

  getTemplateActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    const teamId = data.team_id;

    if (!teamId) {
      this.errorHandler.setValidationError({
        stepId: step?.id,
        taskDefinitionId: step?.taskDefinitionId,
        errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.CONFIG_ERROR'),
        isNonDismissable: true,
      });
      return this.getActionSubtitleEmptyState(step);
    }

    return this.getSalesTeamLink(teamId).pipe(
      switchMap((salesTeam) => {
        return this.getActionSubtitleSuccessState({ salesTeam });
      }),
      catchError((err) => {
        console.error(err);
        return this.getActionSubtitleErrorState(null, step);
      }),
    );
  }

  getActionSubtitleSuccessState(params: any): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.TO_SALES_TEAM', {
      salesTeam: params.salesTeam,
    });
  }

  getActionSubtitleErrorState(params: any, step: WorkflowStepInterface): Observable<string> {
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.CONFIG_ERROR'),
      isNonDismissable: true,
    });
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.A_SALES_TEAM');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASSIGN_SALES_TEAM.A_SALES_TEAM');
  }

  getSalesTeamLink(teamId: string): Observable<string> {
    return this.salesTeamService.getMulti([teamId]).pipe(
      map((teams) => {
        if (teams?.length !== 1 || !teams[0]?.groupId) {
          return 'COMMON.UNKNOWN';
        }
        const team = teams[0];
        return `<a target="_blank" href="https://partners.vendasta.com/st/sales-teams/${teamId}/edit">${team.teamName}</a>`;
      }),
    );
  }
}
